import {
  Popover,
  Accordion,
  Icon,
  Text,
  Transition,
  CloseIconButton,
  useMediaQuery,
} from '@lounge-fe/ui-kit'
import { ProductData } from '../types'
import { useTranslation } from 'react-i18next'
import { Reviews } from '../Reviews'
import { Price } from '../Price'
import { LiquidProductVariant } from '@/features/product/types/LiquidProduct'
import { useEffect, useState } from 'react'
import { useProductPageStore } from '../hooks/useProductPageStore'

export const ProductDetails = ({
  fitAndFabric,
  deliveryReturnsNotices,
  accordions,
  product,
}: ProductDetailsProps) => {
  const { t } = useTranslation()
  const isDesktop = useMediaQuery(({ screens }) => `(min-width: ${screens.xl})`)
  const [openItemKey, setOpenItemKey] = useState<string | null>(null)
  const [isPopoverOpen, setIsPopoverOpen] = useState(false)
  const { setIsDetailsOpen } = useProductPageStore()

  useEffect(() => {
    setIsDetailsOpen(isPopoverOpen)
  }, [isPopoverOpen, setIsDetailsOpen])

  const renderAccordionItems = ({
    withContent,
    onItemClick,
    initialOpenItemKey,
  }: {
    withContent: boolean
    onItemClick?: (key: string) => void
    initialOpenItemKey?: string | null
  }) => {
    const items = []

    items.push(
      <Accordion.Item
        key="details"
        onClick={
          !withContent ? () => onItemClick && onItemClick('details') : undefined
        }
        initialOpen={initialOpenItemKey === 'details'}
        title={<Text variant="title-six">{t('product.details')}</Text>}
      >
        {withContent && product?.description && (
          <div
            className="pb-3"
            dangerouslySetInnerHTML={{
              __html: product?.description,
            }}
          />
        )}
      </Accordion.Item>,
    )

    if (fitAndFabric) {
      items.push(
        <Accordion.Item
          key="fitAndFabric"
          onClick={
            !withContent
              ? () => onItemClick && onItemClick('fitAndFabric')
              : undefined
          }
          initialOpen={initialOpenItemKey === 'fitAndFabric'}
          title={<Text variant="title-six">{t('product.fit_fabric')}</Text>}
        >
          {withContent && (
            <div
              className="pb-4"
              dangerouslySetInnerHTML={{
                __html: fitAndFabric,
              }}
            />
          )}
        </Accordion.Item>,
      )
    }

    if (deliveryReturnsNotices[0]) {
      items.push(
        <Accordion.Item
          key="deliveryReturns"
          onClick={
            !withContent
              ? () => onItemClick && onItemClick('deliveryReturns')
              : undefined
          }
          initialOpen={initialOpenItemKey === 'deliveryReturns'}
          title={
            <Text variant="title-six">
              {deliveryReturnsNotices[0]?.delivery_returns_title}
            </Text>
          }
        >
          {withContent && (
            <ul className="pl-0">
              {deliveryReturnsNotices[0]?.standard_delivery_notice && (
                <li className="flex items-center gap-3 mb-3">
                  <Icon.CycleArrow />
                  <Text variant="body-md">
                    {deliveryReturnsNotices[0]?.standard_delivery_notice}
                  </Text>
                </li>
              )}
              {deliveryReturnsNotices[0]?.next_day_delivery_notice && (
                <li className="flex items-center gap-3 mb-3">
                  <Icon.Truck />
                  <Text variant="body-md">
                    {deliveryReturnsNotices[0]?.next_day_delivery_notice}
                  </Text>
                </li>
              )}
              {deliveryReturnsNotices[0]?.returns_notice && (
                <li className="flex items-center gap-3 mb-3">
                  <Icon.Returns />
                  <Text variant="body-md">
                    {deliveryReturnsNotices[0]?.returns_notice}
                  </Text>
                </li>
              )}
            </ul>
          )}
        </Accordion.Item>,
      )
    }

    accordions.forEach((item, index) => {
      const key = `accordion-${index}`
      items.push(
        <Accordion.Item
          key={key}
          onClick={
            !withContent ? () => onItemClick && onItemClick(key) : undefined
          }
          initialOpen={initialOpenItemKey === key}
          title={<Text variant="title-six">{item.title}</Text>}
        >
          {withContent && (
            <div
              className="pb-3 md:pb-4"
              dangerouslySetInnerHTML={{
                __html: item.content,
              }}
            ></div>
          )}
        </Accordion.Item>,
      )
    })

    items.push(
      <Reviews
        key="reviews"
        onClick={
          !withContent ? () => onItemClick && onItemClick('reviews') : undefined
        }
        productId={product?.id}
        isInitialOpen={initialOpenItemKey === 'reviews'}
      />,
    )

    return <>{items}</>
  }

  return isDesktop ? (
    <Popover scrollLock={false} focusLock={false} exclusive>
      {({ onOpen, onClose, isOpen }) => {
        const handleOnOpen = (key: string) => {
          setOpenItemKey(key)
          setIsPopoverOpen(true)
          onOpen()
        }

        const handleOnClose = () => {
          setOpenItemKey(null)
          setIsPopoverOpen(false)
          onClose()
        }

        return (
          <>
            <Transition.Fade show={!isOpen}>
              {!isOpen && (
                <Accordion
                  closedIcon={<Icon.Plus variant="light" className="w-4 h-4" />}
                  openIcon={<Icon.Minus className="w-4 h-4" />}
                  divider={false}
                >
                  {() =>
                    renderAccordionItems({
                      withContent: false,
                      onItemClick: (key) => {
                        handleOnOpen(key)
                      },
                      initialOpenItemKey: null,
                    })
                  }
                </Accordion>
              )}
            </Transition.Fade>
            <Transition.Fade className="w-full h-full">
              <Popover.Content>
                {isOpen && (
                  <div className="bg-white gap-4 flex flex-col justify-center relative">
                    <div className="flex items-start justify-between">
                      <div>
                        <Text variant="title-five" className="mb-0">
                          {product?.title}
                        </Text>

                        {product?.price && (
                          <Price
                            price={product?.price / 100}
                            compareAtPrice={
                              product?.compare_at_price
                                ? product.compare_at_price / 100
                                : null
                            }
                          />
                        )}
                      </div>
                      <CloseIconButton
                        onClick={handleOnClose}
                        aria-label="Close"
                      />
                    </div>
                    <Accordion
                      className="w-full"
                      closedIcon={
                        <Icon.Plus variant="light" className="w-4 h-4" />
                      }
                      openIcon={<Icon.Minus className="w-4 h-4" />}
                      exclusive
                    >
                      {() =>
                        renderAccordionItems({
                          withContent: true,
                          initialOpenItemKey: openItemKey,
                        })
                      }
                    </Accordion>
                  </div>
                )}
              </Popover.Content>
            </Transition.Fade>
          </>
        )
      }}
    </Popover>
  ) : (
    <Accordion
      className="w-full"
      closedIcon={<Icon.Plus variant="light" className="w-4 h-4" />}
      openIcon={<Icon.Minus className="w-4 h-4" />}
      divider={false}
      exclusive
    >
      {() =>
        renderAccordionItems({
          withContent: true,
        })
      }
    </Accordion>
  )
}

interface ProductDetailsProps {
  fitAndFabric: ProductData['fit_fabric']
  deliveryReturnsNotices: ProductData['delivery_returns_notices']
  accordions: ProductData['accordions']
  product?: ProductData['product']
  selectedVariant?: LiquidProductVariant
}
